import { faExternalLink } from '@fortawesome/free-solid-svg-icons'
import React, { useContext } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import {
  Avatar,
  Button,
  Content,
  Data,
  Layout,
  Navigation,
  Responsive,
  Search,
} from 'ui'
import { truncate } from 'utils'
import { useResourceDocumentsList } from './useResourceDocumentsList'

const { Table, Cell, Row, Header } = Data

export const ResourceListView: React.FC = () => {
  const { useDisplayMode } = useContext(Responsive.DrawerManagerContext)
  useDisplayMode('expanded')
  const navigate = useNavigate()
  const {
    error,
    loading,
    documents,
    locationSearch,
    queryString,
    setQueryString,
    facets,
    toggleFacet,
    appliedFacets,
    hasNextPage,
    hasPreviousPage,
    handleNextPage,
    handlePreviousPage,
    totalCount,
  } = useResourceDocumentsList()
  const handleNewDocument: React.MouseEventHandler = (e) => {
    e.preventDefault()
    navigate('/admin/resources/new')
  }

  return (
    <Layout.PaddedContent className="pb-24 md:pb-32">
      <Helmet>
        <title>Resources - MCSS Pro Connect</title>
      </Helmet>
      <div
        className="flex-col flex w-full"
        style={{ opacity: loading ? 0.5 : 1 }}
      >
        <div className="w-full flex-col flex">
          <Content.Heading className="mt-6 mb-1">Resources</Content.Heading>
          <Content.Text className="pb-2">
            <Link to={`/admin/resources/categories`}>Manage categories</Link>
          </Content.Text>
          {!loading && (
            <Content.Text className="text-sm text-gray-600">
              {totalCount} total documents
            </Content.Text>
          )}
        </div>
        <div className="flex-col flex sm:flex-row flex">
          <div className="w-full sm:w-1/3 md:w-1/4 mr-0 sm:mr-2">
            <Search.FiltersCard
              onToggle={toggleFacet}
              onQuery={setQueryString}
              facets={facets ?? {}}
              appliedFacets={appliedFacets}
              initialQueryString={queryString}
              maxFilterBeforeReveal={5}
            />
          </div>
          <div className="w-full sm:w-2/3 md:w-3/4">
            <div className="flex justify-end mb-3 mt-3 sm:mt-0">
              <Button
                onClick={handleNewDocument}
                className="w-full sm:w-auto"
                variant="default"
              >
                Create Resource
              </Button>
            </div>
            <Table>
              <Row>
                <Header
                  width="40%"
                  className="bg-contentareabackground !text-secondary"
                >
                  Title
                </Header>
                <Header
                  width="30%"
                  className="bg-contentareabackground !text-secondary"
                >
                  Content
                </Header>
                <Header
                  width="10%"
                  className="bg-contentareabackground !text-secondary"
                >
                  &nbsp;
                </Header>
              </Row>
              {documents ? (
                documents.map((doc) => (
                  <Row key={doc.id}>
                    <Cell width="40%">
                      <div className="flex items-center space-x-3">
                        <Avatar.Simple
                          name={doc.title}
                          maxInitials={1}
                          color={doc.color ?? undefined}
                          size={36}
                          imageUrl={doc.icon_url ?? undefined}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-gray-900">
                            {truncate(doc.title ?? '', 50)}
                          </div>
                        </div>
                      </div>
                    </Cell>
                    <Cell width="30%">{truncate(doc.excerpt ?? '', 80)}</Cell>
                    <Cell width="10%">
                      <Navigation.IconLink
                        icon={faExternalLink}
                        to={`/admin/resources/show/${doc.slug}${locationSearch}`}
                      />
                    </Cell>
                  </Row>
                ))
              ) : error ? (
                <Row>
                  <Cell>
                    <div className="text-red-600 p-4 bg-red-50 rounded-md">
                      {error instanceof Error
                        ? error.message
                        : 'There was a problem fetching documents.'}
                    </div>
                  </Cell>
                </Row>
              ) : (
                <Row>
                  <Cell>No results found.</Cell>
                </Row>
              )}
            </Table>
          </div>
        </div>
      </div>
      {!loading && (hasPreviousPage || hasNextPage) && (
        <div className="flex justify-center items-center mt-6">
          <div className="flex gap-2">
            {hasPreviousPage && (
              <button
                onClick={handlePreviousPage}
                className="px-4 py-2 bg-gray-600 text-white font-medium rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors duration-200"
              >
                Previous
              </button>
            )}
            {hasNextPage && (
              <button
                onClick={handleNextPage}
                className="px-4 py-2 bg-teal-600 text-white font-medium rounded-md hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 transition-colors duration-200"
              >
                Next
              </button>
            )}
          </div>
        </div>
      )}
    </Layout.PaddedContent>
  )
}
